import { useEffect, useLayoutEffect, useReducer, useState } from "react";
import Favicon from "react-favicon";
import { Navbar } from "../components/Navbar/Navbar";
import { Centerpanel } from "../components/Centerpanel/Centerpanel";
import {
  initialRouteState,
  RouteActionKind,
  routeReducer,
  ToolType,
} from "../Reducer.ts";
import logoIcon from "../assets/soundscaperLogoPurple.png";
import styles from "./Dashboard.module.css";
import { useNavigate, useParams } from "react-router-dom";
import useEditRouteService from "../api/edits/editRoute.service.ts";
import { Button } from "../components/ui/Button.tsx";
import useFalloffService from "../api/falloffs/falloffs.service.ts";
import { useSaveRoute } from "../hooks/useSaveRoute.ts";

function Dashboard() {
  const [routeState, routeDispatch] = useReducer(
    routeReducer,
    initialRouteState
  );

  // state with language currently used in route
  //NOTE: currently default is en only because the initialRouteState needs the data to load everything
  //if you put just "" and remove the routeTranslation from initialRouteState an undefined error is raised
  const [currentLanguage, setCurrentLanguage] = useState("en");

  //possible switch to react query and replace this in future
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState();

  // state with selected layer
  const [stateSelected, setSelected] = useState("rprop");
  const [currentTool, setCurrentTool] = useState<ToolType>("select");

  const { getRouteState } = useEditRouteService();
  const { getFalloffs } = useFalloffService();
  const { routeId } = useParams();
  const navigate = useNavigate();

  const { handleSaveRoute } = useSaveRoute(
    routeState,
    routeDispatch,
    setCurrentLanguage,
    setSelected
  );

  useEffect(() => {
    handleLoadFalloffs();
    if (routeId) handleLoadRoute(routeId);
  }, [routeId]);

  // Global keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore when typing in inputs
      const target = e.target as HTMLElement;
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable
      )
        return;

      // Ctrl/Cmd+S → save
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        handleSaveRoute();
        return;
      }

      // Allow Escape to stop the walker even when it's running
      if (e.key === "Escape") {
        setCurrentTool("stop");
        return;
      }

      // Don't switch tools when walker is running (p/a/s/l are walker controls)
      if (currentTool === "start") return;

      switch (e.key) {
        case "v":
          setCurrentTool("select");
          break;
        case "g":
          setCurrentTool("move");
          break;
        case "b":
          setCurrentTool("draw");
          break;
        case "d":
          setCurrentTool("delete");
          break;
        case "r":
          setCurrentTool("start");
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [routeState, currentTool, handleSaveRoute]);

  // to set the color of the whole page
  //This covers the bottom white line on the screen, can be removed in future
  useLayoutEffect(() => {
    document.body.style.backgroundColor = "#212121";
    console.log("App LayoutEffect: []");
  });

  // page icon
  const [faviconUrl] = useState(logoIcon);

  let routeName: string = routeState.routeTranslations[currentLanguage].name;

  const handleLoadRoute = async (routeId: string) => {
    try {
      const routeStateData = (await getRouteState(routeId)).data;

      console.log(JSON.stringify(routeStateData, null, 2));

      //After getting data set route state and set current language
      routeDispatch({
        type: RouteActionKind.LOAD_ROUTE,
        payload: routeStateData,
      });
      setCurrentLanguage(Object.keys(routeStateData.routeTranslations)[0]);
    } catch (err: any) {
      //clean up
      console.error("Error loading route:", JSON.stringify(err, null, 2));
      setError(err);
      if (err.response) {
        alert(err.response.data);
      } else {
        console.log(`Error: ${err.message}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoadFalloffs = async () => {
    try {
      const falloffs = (await getFalloffs()).data;
      console.log("All falloff types: ", JSON.stringify(falloffs, null, 2));
      routeDispatch({
        type: RouteActionKind.LOAD_FALLOFFS,
        payload: falloffs,
      });
    } catch (error) {
      console.error("Error loading falloffs:", JSON.stringify(error, null, 2));
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center align-middle text-4xl">
        {" "}
        Loading map....
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-col justify-items-center align-middle ">
        <div className="text-4xl py-20"> Something went wrong.</div>
        <Button
          onClick={() => {
            navigate("/route-select");
          }}
        >
          Go back to route select
        </Button>
      </div>
    );
  }

  return (
    <div className={styles.App}>
      <Favicon url={faviconUrl} />
      <Navbar
        stateRouteName={routeName}
        currentTool={currentTool}
        setCurrentTool={setCurrentTool}
        routeDispatch={routeDispatch}
        routeState={routeState}
        currentLanguage={currentLanguage}
        setCurrentLanguage={setCurrentLanguage}
        stateSelected={stateSelected}
        setSelected={setSelected}
      />
      <Centerpanel
        currentTool={currentTool}
        setCurrentTool={setCurrentTool}
        routeState={routeState}
        routeDispatch={routeDispatch}
        currentLanguage={currentLanguage}
        stateSelected={stateSelected}
        setSelected={setSelected}
      />
    </div>
  );
}

export default Dashboard;
